import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import StaticPageShell, { StaticCtaCard, StaticSection } from '../components/StaticPageShell';

const TermsOfService = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);

    const sections = [
        {
            icon: '🏪',
            titleKey: 'page.terms.marketplace.title',
            titleFb: 'Marketplace role',
            textKey: 'page.terms.marketplace.text',
            textFb:
                'Petito operates as an online marketplace connecting buyers and sellers in Belgium. We are not a party to transactions between users and do not take ownership of listed items.',
        },
        {
            icon: '✅',
            titleKey: 'page.terms.listings.title',
            titleFb: 'Accurate listings & safety',
            textKey: 'page.terms.listings.text',
            textFb:
                'All baby items must be described truthfully, including condition, age, and any defects. Recalled, unsafe, counterfeit, or prohibited products are not allowed. Textile items must be washed before listing. Listings are expected to come from a smoke-free home. Safety equipment that has been in a crash, is expired, or does not meet the required norm may not be listed.',
        },
        {
            icon: '💳',
            titleKey: 'page.terms.payments.title',
            titleFb: 'Payments & fees',
            textKey: 'page.terms.payments.text',
            textFb:
                'Payments are processed via our secure payment partners. Petito may charge a service fee on completed sales. Prices are shown in EUR including applicable Belgian VAT where required.',
        },
        {
            icon: '📋',
            titleKey: 'page.terms.liability.title',
            titleFb: 'Liability',
            textKey: 'page.terms.liability.text',
            textFb:
                'Petito is not responsible for the quality, safety, legality, or delivery of items sold by users. Buyers and sellers resolve disputes directly, with helpdesk support available for escalations.',
        },
        {
            icon: '🚫',
            titleKey: 'page.terms.account.title',
            titleFb: 'Account suspension',
            textKey: 'page.terms.account.text',
            textFb:
                'We may suspend or terminate accounts that violate these terms, our Community Rules, or applicable Belgian and EU law.',
        },
        {
            icon: '🇧🇪',
            titleKey: 'page.terms.law.title',
            titleFb: 'Governing law',
            textKey: 'page.terms.law.text',
            textFb:
                'These terms are governed by Belgian law. Disputes shall be submitted to the competent courts in Belgium, without prejudice to mandatory consumer protections in your country of residence.',
        },
    ];

    return (
        <StaticPageShell
            eyebrow={tr('page.terms.eyebrow', 'Legal')}
            title={tr('page.terms.title', 'Terms of Service')}
            subtitle={tr('page.terms.updated', 'Last updated: August 2026')}
            maxWidth="3xl"
        >
            {sections.map((s) => (
                <StaticSection key={s.titleKey} icon={s.icon} title={tr(s.titleKey, s.titleFb)}>
                    <p>{tr(s.textKey, s.textFb)}</p>
                </StaticSection>
            ))}

            <StaticSection icon="🔐" title={tr('page.terms.accounts.title', 'Accounts & sign-in')}>
                <p>
                    {tr(
                        'page.terms.accounts.text',
                        'You may create and access a Petito shop account using an email and password or by using Continue with Google. By using Google sign-in you confirm you are allowed to use that Google account. If the Google email matches an existing Petito shop account, we may link them. You remain responsible for activity on your Petito account. Use of Google is also subject to Google’s terms. Continue with Google is for the marketplace (shop) only and does not provide access to the Petito administration panel.'
                    )}
                </p>
            </StaticSection>

            <StaticCtaCard>
                <p className="mb-2">
                    {tr('page.terms.related', 'Related documents')}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
                    <Link to="/rules" className="min-h-11 inline-flex items-center">
                        {tr('page.rules.title', 'Community Rules – Petito')}
                    </Link>
                    <span className="hidden text-[var(--text-color)]/40 sm:inline" aria-hidden>
                        ·
                    </span>
                    <Link to="/privacy" className="min-h-11 inline-flex items-center">
                        {tr('page.privacypolicy.title', 'Privacy Policy')}
                    </Link>
                    <span className="hidden text-[var(--text-color)]/40 sm:inline" aria-hidden>
                        ·
                    </span>
                    <Link to="/shipping" className="min-h-11 inline-flex items-center">
                        {tr('page.shipping.title', 'Shipping & Returns')}
                    </Link>
                </div>
            </StaticCtaCard>
        </StaticPageShell>
    );
};

export default TermsOfService;
